/**
 * usePermission Hook
 *
 * Central hook for checking user permissions based on modulePermissions from Redux.
 * Use this hook to control UI visibility and actions based on user's role permissions.
 *
 * @example
 * // Check module-level permission
 * const { canView, canCreate, canEdit, canDelete } = usePermission('manage_companies')
 *
 * // Check submodule-level permission
 * const { canCreate } = usePermission('manage_companies', 'bill_codes')
 *
 * // Use in component
 * {canCreate && <AddButton onClick={handleAdd}>Add Company</AddButton>}
 */

import {useMemo} from 'react'
import {useAppSelector} from '../store/store-hooks'

export interface PermissionCheck {
  /** Can user view this module/feature */
  canView: boolean
  /** Can user create new items in this module/feature */
  canCreate: boolean
  /** Can user edit existing items in this module/feature */
  canEdit: boolean
  /** Can user delete items in this module/feature */
  canDelete: boolean
  /** Is the permission data still loading */
  isLoading: boolean
}

/**
 * Hook to check permissions for a specific module or submodule
 *
 * @param moduleKey - The module key to check (e.g., 'manage_companies', 'job_orders')
 * @param submoduleKey - Optional submodule key (e.g., 'bill_codes', 'positions')
 * @returns PermissionCheck object with canView, canCreate, canEdit, canDelete flags
 */
export const usePermission = (moduleKey: string, submoduleKey?: string): PermissionCheck => {
  const {backendRolePermissions} = useAppSelector((state) => state.root)

  return useMemo(() => {
    // Default to no permissions if no role data
    const defaultPermissions: PermissionCheck = {
      canView: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      isLoading: !backendRolePermissions,
    }

    // If no role permissions loaded yet, return defaults
    if (!backendRolePermissions) {
      return defaultPermissions
    }

    // Check modulePermissions array
    // Note: We no longer bypass for isPlatform because users may have custom
    // permission overrides that should be respected regardless of role category
    const modulePermissions = backendRolePermissions.modulePermissions
    if (!modulePermissions || !Array.isArray(modulePermissions)) {
      // Fallback: Check legacy accessList if modulePermissions not available
      return checkLegacyAccessList(backendRolePermissions.accessList, moduleKey)
    }

    // Find the module at top level
    const modulePermission = modulePermissions.find((m) => m.key === moduleKey)

    if (modulePermission) {
      // If submodule requested, check submodule permissions
      if (submoduleKey && modulePermission.submodules) {
        const submodule = modulePermission.submodules.find((s) => s.key === submoduleKey)
        if (submodule) {
          return {
            canView: submodule.canView ?? false,
            canCreate: submodule.canCreate ?? false,
            canEdit: submodule.canEdit ?? false,
            canDelete: submodule.canDelete ?? false,
            isLoading: false,
          }
        }
        // Submodule not found, return default
        return defaultPermissions
      }

      // Return module-level permissions
      return {
        canView: modulePermission.canView ?? false,
        canCreate: modulePermission.canCreate ?? false,
        canEdit: modulePermission.canEdit ?? false,
        canDelete: modulePermission.canDelete ?? false,
        isLoading: false,
      }
    }

    // Module not found at top level - search in submodules
    // This handles cases like 'company_positions' being a submodule of 'company_manage_profile'
    for (const module of modulePermissions) {
      if (module.submodules && Array.isArray(module.submodules)) {
        const subPermission = module.submodules.find((s) => s.key === moduleKey)
        if (subPermission) {
          return {
            canView: subPermission.canView ?? false,
            canCreate: subPermission.canCreate ?? false,
            canEdit: subPermission.canEdit ?? false,
            canDelete: subPermission.canDelete ?? false,
            isLoading: false,
          }
        }
      }
    }

    // Module not found anywhere
    return defaultPermissions
  }, [backendRolePermissions, moduleKey, submoduleKey])
}

/**
 * Fallback function to check legacy accessList format
 * Used when modulePermissions is not available
 */
const checkLegacyAccessList = (
  accessList: Array<{entityType: string; scope: string; operations: string[]}> | undefined,
  moduleKey: string,
): PermissionCheck => {
  const defaultPermissions: PermissionCheck = {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    isLoading: false,
  }

  if (!accessList || !Array.isArray(accessList)) {
    return defaultPermissions
  }

  // Map moduleKey to entityType
  const entityTypeMap: Record<string, string> = {
    manage_companies: 'Company',
    manage_agencies: 'Agency',
    manage_orders: 'ServiceOrder',
    job_orders: 'JobOrder',
    candidates: 'Applicant',
    users_roles: 'User',
    payroll: 'Timecard',
    positions: 'Position',
  }

  const entityType = entityTypeMap[moduleKey]
  if (!entityType) {
    return defaultPermissions
  }

  const accessEntry = accessList.find(
    (a) => a.entityType.toLowerCase() === entityType.toLowerCase(),
  )

  if (!accessEntry) {
    return defaultPermissions
  }

  const operations = accessEntry.operations.map((op) => op.toLowerCase())

  return {
    canView: operations.includes('read'),
    canCreate: operations.includes('create'),
    canEdit: operations.includes('update') || operations.includes('updatestatus'),
    canDelete: operations.includes('delete'),
    isLoading: false,
  }
}

/**
 * Hook to check if user has ANY of the specified permissions
 * Useful for showing action columns or menus
 *
 * @example
 * const hasAnyAction = useHasAnyPermission('manage_companies', ['canEdit', 'canDelete'])
 * {hasAnyAction && <ActionColumn />}
 */
export const useHasAnyPermission = (
  moduleKey: string,
  permissions: Array<'canView' | 'canCreate' | 'canEdit' | 'canDelete'>,
  submoduleKey?: string,
): boolean => {
  const permissionCheck = usePermission(moduleKey, submoduleKey)

  return permissions.some((perm) => permissionCheck[perm])
}

/**
 * Hook to check if user has ALL of the specified permissions
 *
 * @example
 * const canManage = useHasAllPermissions('manage_companies', ['canEdit', 'canDelete'])
 */
export const useHasAllPermissions = (
  moduleKey: string,
  permissions: Array<'canView' | 'canCreate' | 'canEdit' | 'canDelete'>,
  submoduleKey?: string,
): boolean => {
  const permissionCheck = usePermission(moduleKey, submoduleKey)

  return permissions.every((perm) => permissionCheck[perm])
}

export default usePermission
